import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { loadChecklistData, localizeChecklistData } from '../utils/checklistLoader';

/**
 * Custom hook for managing checklist data with internationalization
 * @param {string} propertyId - The ID of the property to load checklist for
 * @returns {Object} - Hook state and functions
 */
export const useChecklistData = (propertyId) => {
  const { i18n } = useTranslation();
  const [checklistData, setChecklistData] = useState({ categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState(null);

  // Load checklist data when propertyId changes
  useEffect(() => {
    const loadData = async () => {
      if (!propertyId) {
        setChecklistData({ categories: [] });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const data = await loadChecklistData(propertyId);
        setRawData(data);
        
        // Localize the data with current language
        const localizedData = localizeChecklistData(data, i18n.language);
        setChecklistData(localizedData);
      } catch (err) {
        console.error('Error loading checklist data:', err);
        setError(err.message);
        setChecklistData({ categories: [] });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [propertyId, i18n.language]);

  // Re-localize data when language changes
  useEffect(() => {
    if (rawData) {
      const localizedData = localizeChecklistData(rawData, i18n.language);
      setChecklistData(localizedData);
    }
  }, [i18n.language, rawData]);

  return {
    checklistData,
    loading,
    error,
    currentLanguage: i18n.language
  };
};